import {
  hasPermission,
  MAX_SCHEDULE_TASKS,
  MAX_TASK_OFFSET_SECONDS,
  permissionForTask,
  scheduleSchema,
  scheduleTaskSchema,
  type Schedule,
  type ScheduleTask,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CalendarClock, Clock, History, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { QueryState } from '@/components/query-state';
import { StatTile } from '@/components/stat-tile';
import { StatusPill } from '@/components/status-badges';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ApiRequestError, apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { describeCron } from '@/lib/cron-text';
import { dateTime, timeAgo } from '@/lib/format';
import { serverQuery } from '@/lib/server-queries';

export const Route = createFileRoute('/_app/server/$server/schedules')({ component: SchedulesTab });

const PRESETS: { label: string; cron: Schedule['cron'] }[] = [
  {
    label: 'Every 5 minutes',
    cron: { minute: '*/5', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*' },
  },
  {
    label: 'Every hour',
    cron: { minute: '0', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*' },
  },
  {
    label: 'Every day at 04:00',
    cron: { minute: '0', hour: '4', dayOfMonth: '*', month: '*', dayOfWeek: '*' },
  },
  {
    label: 'Every Monday at 04:00',
    cron: { minute: '0', hour: '4', dayOfMonth: '*', month: '*', dayOfWeek: '1' },
  },
];

/** What runs next and what ran last, across every schedule. */
function ScheduleSummary({ schedules }: { schedules: Schedule[] }) {
  const active = schedules.filter((s) => s.isActive);
  const next = active
    .map((s) => s.nextRunAt)
    .filter((t): t is string => t !== null)
    .sort()[0];
  const last = schedules
    .map((s) => s.lastRunAt)
    .filter((t): t is string => t !== null)
    .sort()
    .at(-1);
  const nextName = next && active.find((s) => s.nextRunAt === next)?.name;
  const lastName = last && schedules.find((s) => s.lastRunAt === last)?.name;
  return (
    <section aria-label="Schedule summary" className="grid gap-3 sm:grid-cols-3">
      <StatTile
        icon={CalendarClock}
        label="Active"
        value={active.length}
        limit={`of ${schedules.length}`}
        detail={
          active.length === schedules.length ? 'All schedules are on' : 'Paused schedules don’t run'
        }
      />
      <StatTile
        icon={Clock}
        label="Next run"
        value={next ? timeAgo(next) : '—'}
        detail={next ? `${nextName} · ${dateTime(next)}` : 'Nothing is scheduled'}
      />
      <StatTile
        icon={History}
        label="Last run"
        value={last ? timeAgo(last) : '—'}
        detail={last ? `${lastName} · ${dateTime(last)}` : 'No schedule has run yet'}
      />
    </section>
  );
}

const cronText = (c: Schedule['cron']) =>
  `${c.minute} ${c.hour} ${c.dayOfMonth} ${c.month} ${c.dayOfWeek}`;

function SchedulesTab() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const queryClient = useQueryClient();
  const key = ['server', id, 'schedules'];
  const list = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      apiRequest(`/api/client/servers/${id}/schedules`, {
        schema: z.array(scheduleSchema),
        signal,
      }),
    refetchInterval: (q) => (q.state.data?.some((s) => s.isProcessing) ? 2000 : 15_000),
  });
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(server.permissions, p);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });

  const run = useMutation({
    mutationFn: (s: Schedule) =>
      apiRequest(`/api/client/servers/${id}/schedules/${s.id}/execute`, {
        method: 'POST',
        schema: z.null(),
      }),
    onSuccess: () => {
      toast.success('Running now.');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const toggle = useMutation({
    mutationFn: (s: Schedule) =>
      apiRequest(`/api/client/servers/${id}/schedules/${s.id}`, {
        method: 'PUT',
        body: {
          name: s.name,
          cron: s.cron,
          onlyWhenOnline: s.onlyWhenOnline,
          isActive: !s.isActive,
        },
        schema: scheduleSchema,
      }),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (s: Schedule) =>
      apiRequest(`/api/client/servers/${id}/schedules/${s.id}`, {
        method: 'DELETE',
        schema: z.null(),
      }),
    onSuccess: () => {
      toast.success('Schedule deleted.');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          Run commands, power actions and backups on a timetable. Times are in UTC.
        </p>
        {can('schedule.create') && (
          <Button onClick={() => setEditing('new')}>
            <Plus /> New schedule
          </Button>
        )}
      </div>
      <QueryState query={list}>
        {(schedules) =>
          schedules.length === 0 ? (
            <Card className="bg-card/50 border-dashed shadow-none">
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <span className="bg-elevated text-muted-foreground grid size-11 place-items-center rounded-lg border">
                  <CalendarClock className="size-6" aria-hidden />
                </span>
                <p className="font-medium">No schedules yet</p>
                <p className="text-muted-foreground max-w-sm text-sm">
                  For example: warn players, back up, then restart every night.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              <ScheduleSummary schedules={schedules} />
              <ul className="grid gap-3">
                {schedules.map((s) => (
                  <li key={s.id}>
                    <Card className="gap-3 px-4 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="grid min-w-0 gap-1">
                          <p className="flex flex-wrap items-center gap-2 font-medium">
                            <span className="truncate">{s.name}</span>
                            {s.isProcessing && (
                              <StatusPill tone="warning" pulse>
                                Running
                              </StatusPill>
                            )}
                            {s.onlyWhenOnline && (
                              <Badge variant="secondary">Only when online</Badge>
                            )}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {describeCron(s.cron) ?? 'Custom timing (UTC)'}
                            <span className="text-subtle font-mono"> · {cronText(s.cron)}</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {can('schedule.update') && (
                            <Switch
                              checked={s.isActive}
                              onCheckedChange={() => toggle.mutate(s)}
                              aria-label={`${s.name} is ${s.isActive ? 'active' : 'paused'}`}
                            />
                          )}
                          {can('schedule.update') && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={s.isProcessing || s.tasks.length === 0}
                              onClick={() => run.mutate(s)}
                            >
                              <Play /> Run now
                            </Button>
                          )}
                          {can('schedule.update') && (
                            <Button variant="outline" size="sm" onClick={() => setEditing(s.id)}>
                              <Pencil /> Edit
                            </Button>
                          )}
                          {can('schedule.delete') && (
                            <Confirm
                              title={`Delete “${s.name}”?`}
                              description="Its tasks are removed too."
                              action="Delete"
                              destructive
                              onConfirm={() => remove.mutate(s)}
                            >
                              <Button
                                variant="outline-destructive"
                                size="icon"
                                aria-label={`Delete ${s.name}`}
                              >
                                <Trash2 />
                              </Button>
                            </Confirm>
                          )}
                        </div>
                      </div>
                      <dl className="text-muted-foreground grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
                        <div>
                          <dt>Last run</dt>
                          <dd className="text-foreground" title={s.lastRunAt ?? undefined}>
                            {s.lastRunAt ? timeAgo(s.lastRunAt) : 'Never'}
                          </dd>
                        </div>
                        <div>
                          <dt>Next run</dt>
                          <dd className="text-foreground">
                            {s.isActive && s.nextRunAt ? dateTime(s.nextRunAt) : 'Paused'}
                          </dd>
                        </div>
                        <div>
                          <dt>Tasks</dt>
                          <dd className="text-foreground">
                            {s.tasks.length === 0
                              ? 'None yet'
                              : s.tasks
                                  .map((t) => (t.action === 'power' ? t.payload : t.action))
                                  .join(' → ')}
                          </dd>
                        </div>
                      </dl>
                    </Card>
                  </li>
                ))}
              </ul>
            </div>
          )
        }
      </QueryState>
      {editing !== null && (
        <ScheduleDialog
          server={id}
          schedule={editing === 'new' ? null : (list.data?.find((s) => s.id === editing) ?? null)}
          mine={server.permissions}
          onChanged={refresh}
          onClose={() => setEditing(null)}
          onCreated={(s) => setEditing(s.id)}
        />
      )}
    </div>
  );
}

function ScheduleDialog({
  server,
  schedule,
  mine,
  onChanged,
  onClose,
  onCreated,
}: {
  server: string;
  schedule: Schedule | null;
  mine: string[];
  onChanged: () => void;
  onClose: () => void;
  onCreated: (s: Schedule) => void;
}) {
  const [name, setName] = useState(schedule?.name ?? '');
  const [cron, setCron] = useState<Schedule['cron']>(schedule?.cron ?? PRESETS[2]!.cron);
  const [onlyWhenOnline, setOnlyWhenOnline] = useState(schedule?.onlyWhenOnline ?? false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/schedules${schedule ? `/${schedule.id}` : ''}`, {
        method: schedule ? 'PUT' : 'POST',
        body: { name, cron, onlyWhenOnline, isActive: schedule?.isActive ?? true },
        schema: scheduleSchema,
      }),
    onSuccess: (s) => {
      setErrors({});
      onChanged();
      if (schedule) {
        toast.success('Schedule saved.');
        onClose();
      } else {
        toast.success('Created. Now add its tasks.');
        onCreated(s);
      }
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.body?.fields) {
        setErrors(
          Object.fromEntries(
            Object.entries(e.body.fields).map(([k, v]) => [
              k.replace('body.cron.', ''),
              v[0] ?? '',
            ]),
          ),
        );
      } else toast.error(errorMessage(e));
    },
  });
  const fields = [
    ['minute', 'Minute'],
    ['hour', 'Hour'],
    ['dayOfMonth', 'Day of month'],
    ['month', 'Month'],
    ['dayOfWeek', 'Day of week'],
  ] as const;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{schedule ? `Edit ${schedule.name}` : 'New schedule'}</DialogTitle>
          <DialogDescription>
            Cron timing in UTC. Use <code className="font-mono">*</code> for “every”,{' '}
            <code className="font-mono">*/5</code> for “every 5th”, or lists like{' '}
            <code className="font-mono">1,15</code>.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <FormField label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={191}
            />
          </FormField>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <Button
                key={p.label}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setCron(p.cron)}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {fields.map(([k, label]) => (
              <FormField key={k} label={label} {...(errors[k] ? { error: errors[k] } : {})}>
                <Input
                  className="font-mono"
                  value={cron[k]}
                  onChange={(e) => setCron({ ...cron, [k]: e.target.value })}
                />
              </FormField>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Switch id="only-online" checked={onlyWhenOnline} onCheckedChange={setOnlyWhenOnline} />
            <Label htmlFor="only-online">Only run while the server is online</Label>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={save.isPending || !name}>
              {save.isPending ? 'Saving…' : schedule ? 'Save timing' : 'Create schedule'}
            </Button>
          </div>
        </form>
        {schedule && (
          <TaskEditor server={server} schedule={schedule} mine={mine} onChanged={onChanged} />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskEditor({
  server,
  schedule,
  mine,
  onChanged,
}: {
  server: string;
  schedule: Schedule;
  mine: string[];
  onChanged: () => void;
}) {
  const base = `/api/client/servers/${server}/schedules/${schedule.id}/tasks`;
  const add = useMutation({
    mutationFn: () =>
      apiRequest(base, {
        method: 'POST',
        body: {
          action: 'command',
          payload: 'say Hello',
          timeOffsetSeconds: 0,
          continueOnFailure: false,
        },
        schema: scheduleTaskSchema,
      }),
    onSuccess: onChanged,
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <section className="grid gap-3 border-t pt-4" aria-label="Tasks">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Tasks, in order</h3>
        <Button
          size="sm"
          variant="outline"
          disabled={add.isPending || schedule.tasks.length >= MAX_SCHEDULE_TASKS}
          onClick={() => add.mutate()}
        >
          <Plus /> Add task
        </Button>
      </div>
      {schedule.tasks.length === 0 && (
        <p className="text-muted-foreground text-sm">
          No tasks yet. Add one to give the schedule something to do.
        </p>
      )}
      {schedule.tasks.map((t, i) => (
        <TaskRow
          key={`${t.id}:${t.action}:${t.payload}:${t.timeOffsetSeconds}:${t.continueOnFailure}`}
          base={base}
          task={t}
          index={i}
          mine={mine}
          onChanged={onChanged}
        />
      ))}
    </section>
  );
}

function TaskRow({
  base,
  task,
  index,
  mine,
  onChanged,
}: {
  base: string;
  task: ScheduleTask;
  index: number;
  mine: string[];
  onChanged: () => void;
}) {
  const [action, setAction] = useState(task.action);
  const [payload, setPayload] = useState(task.payload);
  const [offset, setOffset] = useState(String(task.timeOffsetSeconds));
  const [cont, setCont] = useState(task.continueOnFailure);
  const dirty =
    action !== task.action ||
    payload !== task.payload ||
    Number(offset) !== task.timeOffsetSeconds ||
    cont !== task.continueOnFailure;
  const allowed = (a: ScheduleTask['action'], p: string) =>
    hasPermission(mine, permissionForTask(a, p));
  const save = useMutation({
    mutationFn: () =>
      apiRequest(`${base}/${task.id}`, {
        method: 'PUT',
        body: { action, payload, timeOffsetSeconds: Number(offset) || 0, continueOnFailure: cont },
        schema: scheduleTaskSchema,
      }),
    onSuccess: () => {
      toast.success(`Task ${index + 1} saved.`);
      onChanged();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => apiRequest(`${base}/${task.id}`, { method: 'DELETE', schema: z.null() }),
    onSuccess: onChanged,
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card className="gap-3 p-3">
      <div className="grid gap-3 sm:grid-cols-[8rem_1fr_7rem]">
        <FormField label={`Task ${index + 1}`}>
          <Select
            value={action}
            onValueChange={(v) => {
              const next = v as ScheduleTask['action'];
              setAction(next);
              setPayload(next === 'power' ? 'restart' : next === 'command' ? 'say Hello' : '');
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="command">Send command</SelectItem>
              <SelectItem value="power">Power action</SelectItem>
              <SelectItem value="backup">Create backup</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        {action === 'command' && (
          <FormField label="Command">
            <Input
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              className="font-mono"
              maxLength={4096}
            />
          </FormField>
        )}
        {action === 'power' && (
          <FormField label="Action">
            <Select value={payload} onValueChange={setPayload}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['start', 'restart', 'stop', 'kill'] as const).map((a) => (
                  <SelectItem key={a} value={a} disabled={!allowed('power', a)}>
                    {a[0]!.toUpperCase() + a.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        )}
        {action === 'backup' && (
          <FormField label="Files to leave out" description="One pattern per line.">
            <Textarea
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              rows={2}
              className="font-mono text-xs"
            />
          </FormField>
        )}
        <FormField label="Wait first (s)" description={`0–${MAX_TASK_OFFSET_SECONDS}`}>
          <Input
            type="number"
            min={0}
            max={MAX_TASK_OFFSET_SECONDS}
            value={offset}
            onChange={(e) => setOffset(e.target.value)}
          />
        </FormField>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Switch id={`cont-${task.id}`} checked={cont} onCheckedChange={setCont} />
          <Label htmlFor={`cont-${task.id}`} className="text-xs font-normal">
            Keep going if this fails
          </Label>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={!dirty || save.isPending || !allowed(action, payload)}
            onClick={() => save.mutate()}
          >
            Save task
          </Button>
          <Button
            size="icon"
            variant="outline-destructive"
            aria-label={`Remove task ${index + 1}`}
            onClick={() => remove.mutate()}
          >
            <Trash2 />
          </Button>
        </div>
      </div>
      {!allowed(action, payload) && (
        <p className="text-muted-foreground text-xs">
          You don’t have the permission this task needs.
        </p>
      )}
    </Card>
  );
}
